// @flow

import React, { type Node } from 'react';
import { Empty } from '@tectonics/components';
import { type Country } from '@tectonics/prs/country';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { IfFeature } from '@tectonics/prs/feature';
import { amountToCurrencyString } from '@tectonics/prs/money';
import { useCurrentReceipt, type InstalmentsPlan } from '@tectonics/prs/receipts';
import { getInstalmentsPlan } from '@tectonics/prs/receipts/receipt';
import { getInstalmentsNumber, getAmountPartialFirst } from '@tectonics/prs/receipts/receipt/instalments-plan';
import { Portal } from '@tectonics/commons/portal';
import { getMoneyFormat } from '../../../utils/money-format';

export function SelectedInstalmentDisplay(): Node {
  const allFeatures = useAllFeatures();
  return (
    <IfFeature allFeatures={allFeatures} wanted="display_selected_instalment">
      <SelectedInstalmentComponent />
    </IfFeature>
  );
}

function SelectedInstalmentComponent(): Node {
  const currentReceipt = useCurrentReceipt();
  const instalmentsPlan = getInstalmentsPlan(currentReceipt);
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  if (!instalmentsPlan || getInstalmentsNumber(instalmentsPlan) === 1) {
    return <Empty />;
  }

  const rowStyle = { paddingTop: '2em' };

  return (
    <Portal id="bromelia-selected-instalment-display">
      <th className="total-line__name" scope="row" style={rowStyle}>
        <span><InstalmentsLabelText country={country} /></span>
      </th>
      <th className="total-line__name" style={rowStyle}>
        <span className="order-summary__emphasis">
          <CurrentInstalmentText instalmentsPlan={instalmentsPlan} />
        </span>
      </th>
    </Portal>
  );
}

type CurrentInstalmentTextProps = {|
  instalmentsPlan: InstalmentsPlan,
|};

function CurrentInstalmentText({ instalmentsPlan }: CurrentInstalmentTextProps): Node {
  const formatAmount = amountToCurrencyString(getMoneyFormat('br'));
  return `${getInstalmentsNumber(instalmentsPlan)}x de ${formatAmount(getAmountPartialFirst(instalmentsPlan))}`;
}

function InstalmentsLabelText(props: { country: Country }): Node {
  switch (props.country) {
    case 'br':
      return 'Parcelas';
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'uy':
      return 'Cuotas';
    default:
      throw new Error(`Country "${props.country}" no supported`);
  }
}
